import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, Brain, CheckCircle2, Clock, 
  AlertCircle, Trophy, ArrowRight, User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmployeeDashboard = () => {
  const { user, company } = useAuth();
  const [data, setData] = useState({
    pendingTrainings: [],
    pendingAssessments: [],
    stats: { points: 0, trainingsCompleted: 0, assessmentsCompleted: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/employee-portal/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      // Use mock data if API fails
      setData({
        pendingTrainings: [],
        pendingAssessments: [],
        stats: { points: 0, trainingsCompleted: 0, assessmentsCompleted: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const hasPendingTasks = (data.pendingTrainings?.length > 0) || (data.pendingAssessments?.length > 0);

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0d9488] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <User size={32} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              Olá, {user?.name || 'Funcionário'}!
            </h1>
            <p className="text-white/80">
              {company?.nome || 'Sua Empresa'} • Portal do Funcionário
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4 text-center">
            <Trophy size={28} className="mx-auto mb-2" />
            <p className="text-3xl font-bold">{data.stats?.points || 0}</p>
            <p className="text-sm opacity-80">Pontos</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4 text-center">
            <GraduationCap size={28} className="mx-auto mb-2" />
            <p className="text-3xl font-bold">{data.stats?.trainingsCompleted || 0}</p>
            <p className="text-sm opacity-80">Treinamentos</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4 text-center">
            <Brain size={28} className="mx-auto mb-2" />
            <p className="text-3xl font-bold">{data.stats?.assessmentsCompleted || 0}</p>
            <p className="text-sm opacity-80">Avaliações</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Tasks Alert */}
      {hasPendingTasks && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-amber-600" size={28} />
              <div className="flex-1">
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Você tem tarefas pendentes!
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {data.pendingTrainings?.length || 0} treinamento(s) e {data.pendingAssessments?.length || 0} avaliação(ões) aguardando.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Trainings */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap size={20} className="text-emerald-600" />
              Treinamentos Pendentes
            </CardTitle>
            <Link to="/meus-treinamentos">
              <Button variant="ghost" size="sm">
                Ver todos <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {data.pendingTrainings?.length > 0 ? (
            <div className="space-y-3">
              {data.pendingTrainings.slice(0, 3).map((training, index) => (
                <div 
                  key={training.id || index} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    training.tipo === 'dds' ? 'border-l-4 border-l-amber-500 bg-amber-50/50' : 'bg-slate-50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{training.titulo}</span>
                      {training.tipo === 'dds' && (
                        <Badge className="bg-amber-500 text-xs">DDS</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {training.carga_horaria || training.duracao_minutos || 60} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Trophy size={12} />
                        +{training.pontos || 10} pts
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    className={training.tipo === 'dds' ? 'bg-amber-600' : 'bg-emerald-600'}
                  >
                    Iniciar
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-green-500" />
              <p>Nenhum treinamento pendente!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Assessments */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Brain size={20} className="text-purple-600" />
              Avaliações Pendentes
            </CardTitle>
            <Link to="/minhas-avaliacoes">
              <Button variant="ghost" size="sm">
                Ver todas <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {data.pendingAssessments?.length > 0 ? (
            <div className="space-y-3">
              {data.pendingAssessments.slice(0, 3).map((assessment, index) => (
                <div 
                  key={assessment.id || index} 
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border"
                >
                  <div className="flex-1">
                    <span className="font-medium">{assessment.titulo || assessment.nome}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{assessment.tipo}</Badge>
                    </div>
                  </div>
                  <Button size="sm" className="bg-purple-600">
                    Responder
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-green-500" />
              <p>Nenhuma avaliação pendente!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;
